import React, { useEffect, useRef, useState } from "react";
import "./QuickAdd.css";
import PlusIconBlue from "../common/PlusIconBlue/PlusIconBlue";
import { useNavigate } from "react-router-dom";
import { QUICK_ADD_CONFIG, useQuickAddStore } from "../../store/quickAddStore";
import ErrorNotification from "../common/ErrorAlert/ErrorNotification";
import { useTranslation } from "react-i18next";

const QuickAddButton = ({ ...props }) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("sales");
  const [fieldErrors, setFieldErrors] = useState([]);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const configRef = useRef(null);

  // Zustand store hooks
  const {
    enabledConfig,
    toggleSection,
    toggleItem,
    getEnabledSections,
    getEnabledItemsForSection,
  } = useQuickAddStore();

  const navigateToPage = (path) => {
    console.log(`Navigate to ${path}`);
    navigate(path);
    setIsDropdownOpen(false);
  };

  // Function to get total enabled sections count
  const getTotalEnabledSectionsCount = () => {
    return getEnabledSections().length;
  };

  // Check if section is enabled (appears in menu)
  const isSectionEnabled = (sectionKey) => {
    return getEnabledSections().includes(sectionKey);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (configRef.current && !configRef.current.contains(event.target)) {
        setIsConfigOpen(false);
        clearErrors();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const showError = (message) => {
    setFieldErrors({ message });
    setTimeout(() => {
      setFieldErrors({});
    }, 5000);
  };

  // Clear errors
  const clearErrors = () => {
    setFieldErrors([]);
  };

  const handleConfigClick = () => {
    setIsDropdownOpen(false);
    setIsConfigOpen(true);
    clearErrors();
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleToggleItem = (sectionKey, itemKey, isChecked) => {
    const isSectionCurrentlyEnabled = isSectionEnabled(sectionKey);

    if (isChecked && !isSectionCurrentlyEnabled) {
      const currentSectionCount = getTotalEnabledSectionsCount();
      if (currentSectionCount >= 5) {
        showError(t("Maximum 5 menus allowed. Please disable a menu first."));
        return;
      }
    }

    toggleItem(sectionKey, itemKey, isChecked);
  };

  const handleToggleSection = (sectionKey, isChecked) => {
    if (isChecked) {
      const currentSectionCount = getTotalEnabledSectionsCount();
      const isSectionCurrentlyEnabled = isSectionEnabled(sectionKey);

      if (!isSectionCurrentlyEnabled && currentSectionCount >= 5) {
        showError(t("Maximum 5 menus allowed. Please disable a menu first."));
        return;
      }
    }

    toggleSection(sectionKey, isChecked);
  };

  // Handle save button
  const handleSave = () => {
    setIsConfigOpen(false);
    clearErrors();
  };

  // Handle cancel button
  const handleCancel = () => {
    setIsConfigOpen(false);
    clearErrors();
  };

  // Set first enabled section as active when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      const enabledSections = getEnabledSections();
      if (enabledSections.length > 0 && !enabledSections.includes(activeTab)) {
        setActiveTab(enabledSections[0]);
      }
    }
  }, [isDropdownOpen, enabledConfig, activeTab, getEnabledSections]);

  return (
    <div className="quick-add-container" ref={dropdownRef}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`quick-add-icon ${isDropdownOpen ? "active" : ""} `}
        width="24"
        height="24"
        viewBox="0 0 28 28"
        fill="none"
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        {...props}
      >
        <rect width="28" height="28" rx="14" fill="#FFAB00" />
        <path
          d="M14 7L14 21"
          stroke="black"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M21 14L7 14"
          stroke="black"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* Main Quick Add Dropdown */}
      {isDropdownOpen && (
        <div className="quick-add-dropdown">
          <div className="quick-add-section">
            <div className="quick-add-head">
              {getEnabledSections().map((section) => (
                <span
                  key={section}
                  className={` tab-item ${activeTab === section ? "active" : ""}`}
                  onClick={() => handleTabClick(section)}
                >
                  {t(QUICK_ADD_CONFIG[section].label)}
                </span>
              ))}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 23 23"
                fill="none"
                className="config-icon"
                onClick={handleConfigClick}
                style={{ cursor: "pointer" }}
                data-tooltip-id="global-tooltip"
                data-tooltip-content={t("Smart Add")}
              >
                <rect width="23" height="23" rx="2" fill="#094C80" />
                <path
                  d="M18 7.25H11.25"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.5 14.75H6.75"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.75 17C16.9926 17 18 15.9926 18 14.75C18 13.5074 16.9926 12.5 15.75 12.5C14.5074 12.5 13.5 13.5074 13.5 14.75C13.5 15.9926 14.5074 17 15.75 17Z"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.25 9.5C9.49264 9.5 10.5 8.49264 10.5 7.25C10.5 6.00736 9.49264 5 8.25 5C7.00736 5 6 6.00736 6 7.25C6 8.49264 7.00736 9.5 8.25 9.5Z"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="quick-add-body">
              <div className="quick-form-row">
                {getEnabledItemsForSection(activeTab).map((itemKey) => {
                  const item = QUICK_ADD_CONFIG[activeTab].items[itemKey];
                  return (
                    <div
                      key={itemKey}
                      className="quick-link"
                      onClick={() => navigateToPage(item.path)}
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content={t(`Create ${item.label}`)}
                    >
                      <PlusIconBlue className="quick-add-plus" />
                      <span className="quick-content ">
                        {t(item.label)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {isConfigOpen && (
        <div className="config-overlay">
          <div className="config-modal" ref={configRef}>
            <div className="config-header ">
              <h3>{t("SMART ADD")}</h3>
              <p>
                {t(
                  "Choose the actions you want to access quickly from the menu. Select the modules that matter most to your workflow, so you can create orders, entries, or tasks faster and with fewer steps. Maximum 5 menus allowed.",
                )}
              </p>
            </div>
            <div className="error-section">
              <ErrorNotification errors={fieldErrors} marginBottom={"1rem"} />
            </div>
            <div className="config-body">
              {Object.keys(QUICK_ADD_CONFIG).map((sectionKey) => {
                const section = QUICK_ADD_CONFIG[sectionKey];

                return (
                  <div key={sectionKey} className="config-section">
                    <div className="config-section-header">
                      <span className=" ftw-4">{t(section.label)}</span>
                      <div className="checkbox-with-tooltip">
                        <input
                          type="checkbox"
                          className="gold"
                          checked={isSectionEnabled(sectionKey)}
                          onChange={(e) =>
                            handleToggleSection(sectionKey, e.target.checked)
                          }
                          data-tooltip-id="global-tooltip"
                          data-tooltip-content={
                            isSectionEnabled(sectionKey)
                              ? t("Disable Section")
                              : t("Enable Section")
                          }
                        />
                        {/* <div className="quick-tooltip ">
                          {t("Select to enable or disable this section in Quick Add.")}
                        </div> */}
                      </div>
                    </div>
                    <div className="config-items">
                      {Object.keys(section.items).map((itemKey) => {
                        const item = section.items[itemKey];
                        const isChecked =
                          enabledConfig[sectionKey]?.items?.includes(itemKey) ||
                          false;

                        return (
                          <div key={itemKey} className="quick-form-row">
                            <input
                              type="checkbox"
                              className="cursor"
                              id={`selectForMenu-${sectionKey}-${itemKey}`}
                              checked={isChecked}
                              onChange={(e) =>
                                handleToggleItem(
                                  sectionKey,
                                  itemKey,
                                  e.target.checked,
                                )
                              }
                            />
                            <label
                              htmlFor={`selectForMenu-${sectionKey}-${itemKey}`}
                              className=" quick-content cursor"
                            >
                              {t(item.label)}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="config-footer">
              <span
                className="menu-counter "
              >
                {`${getTotalEnabledSectionsCount()}/5 ${t("menus selected")}`}
              </span>
              <button
                className="quick-cancel-btn "
                onClick={handleCancel}
              >
                {t("Cancel")}
              </button>
              <button className="quick-save-btn " onClick={handleSave}>
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickAddButton;
